import type {
  PlatformIdentityState,
  SyncMutation,
  SyncQueue,
} from "./types";

export const MAX_SYNC_QUEUE_ITEMS = 2_000;

export function createGuestPlatformState(
  online: boolean,
): PlatformIdentityState {
  return {
    authentication: "guest",
    synchronization: online ? "local-only" : "offline",
    online,
    accountId: null,
    email: null,
    displayName: null,
    canSignIn: false,
    lastSyncedAt: null,
    errorCode: null,
    label: online ? "Local guest" : "Offline · local guest",
    detail: online
      ? "Progress is saved in this browser. Account sync is not configured."
      : "You can keep practicing. New progress stays in this browser until you are online again.",
  };
}
export function createSyncQueue(): SyncQueue {
  return {
    schemaVersion: 1,
    items: [],
  };
}

export function enqueueSyncMutation(
  queue: SyncQueue,
  mutation: Omit<SyncMutation, "attempts" | "lastErrorCode">,
): SyncQueue {
  const withoutDuplicate = queue.items.filter((item) => item.id !== mutation.id);
  return {
    schemaVersion: 1,
    items: [
      ...withoutDuplicate,
      {
        ...structuredClone(mutation),
        attempts: 0,
        lastErrorCode: null,
      },
    ].slice(-MAX_SYNC_QUEUE_ITEMS),
  };
}

export function acknowledgeSyncMutation(
  queue: SyncQueue,
  mutationId: string,
): SyncQueue {
  return {
    schemaVersion: 1,
    items: queue.items.filter((item) => item.id !== mutationId),
  };
}

export function markSyncMutationFailed(
  queue: SyncQueue,
  mutationId: string,
  errorCode: string,
  now: Date,
): SyncQueue {
  return {
    schemaVersion: 1,
    items: queue.items.map((item) => {
      if (item.id !== mutationId) {
        return structuredClone(item);
      }
      const attempts = item.attempts + 1;
      const retryDelayMs = Math.min(60 * 60 * 1_000, 2 ** attempts * 5_000);
      return {
        ...structuredClone(item),
        attempts,
        lastErrorCode: errorCode.slice(0, 80),
        nextAttemptAt: new Date(now.getTime() + retryDelayMs).toISOString(),
      };
    }),
  };
}

export function getReadySyncMutations(
  queue: SyncQueue,
  now: Date,
): SyncMutation[] {
  return queue.items
    .filter((item) => Date.parse(item.nextAttemptAt) <= now.getTime())
    .map((item) => structuredClone(item));
}
